import { IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { CheckCircleOutline, MoreVert, OpenInNew, Reply } from '@material-ui/icons';
import { Layout, useBaseStore, useUserStore } from '@roc/feature-app-core';
import { PostQcReviewDialog } from '@roc/feature-ai-docs-review';
import { DocumentName } from '@roc/feature-documents';
import { TaskStatus } from '@roc/feature-general-tasks';
import { Document } from '@roc/feature-types';
import { formatDate } from '@roc/feature-utils';
import {
  ConfirmDialog, DataGrid, GridColumnProps, SelectFilterComponent, SelectFloatingFilterComponent, StatusChip, StatusType,
} from '@roc/ui';
import { noOpComparator } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useQcDashboardStore } from '../hooks/useQcDashboardStore';
import { FLAGGED_ITEMS_BLOCK_COMPLETION, QC_COMPLETED_STATUS, QC_STATUS_OPTIONS, QcDashboardRow } from '../stores/qcDashboardStore';

const NO_ITEMS_FOR_REVISION = 'Nothing marked for revision';
const REVIEW_ALREADY_COMPLETED = 'This review is already completed';

const useStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  menuIcon: {
    minWidth: 32,
  },
}));

const textFilter = {
  filter: 'agTextColumnFilter',
  floatingFilter: true,
  floatingFilterComponentParams: { suppressFilterButton: true },
};

const columns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 110,
    maxWidth: 140,
    cellRenderer: 'loanIdCellRenderer',
    comparator: noOpComparator,
    ...textFilter,
  },
  {
    field: 'propertyAddress',
    headerName: 'Property Address',
    minWidth: 320,
    flex: 1,
    comparator: noOpComparator,
    ...textFilter,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 170,
    maxWidth: 200,
    cellRenderer: 'statusCellRenderer',
    comparator: noOpComparator,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: QC_STATUS_OPTIONS,
      testId: 'qc-status-filter',
    },
  },
  {
    field: 'qcSpecialist',
    headerName: 'QC Specialist',
    minWidth: 200,
    maxWidth: 260,
    cellRenderer: 'qcSpecialistCellRenderer',
    comparator: noOpComparator,
    ...textFilter,
  },
  {
    field: 'delayMinutes',
    headerName: 'Delay',
    minWidth: 150,
    maxWidth: 180,
    cellRenderer: 'delayCellRenderer',
    comparator: noOpComparator,
  },
  {
    field: 'uploadedDate',
    headerName: 'Uploaded Date',
    minWidth: 170,
    maxWidth: 200,
    cellRenderer: 'uploadedDateCellRenderer',
    comparator: noOpComparator,
  },
  {
    field: 'taskId',
    headerName: 'Actions',
    minWidth: 100,
    maxWidth: 110,
    cellRenderer: 'actionsCellRenderer',
    sortable: false,
    pinned: 'right',
    lockPinned: true,
  },
];

const formatDelay = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${Math.floor(hours / 24)} days ${hours % 24} hours` : `${minutes} minutes`;
};

const loanUrl = (loanId: number) => {
  const parentOrigin = window.location.ancestorOrigins?.[0] ?? window.location.origin;
  return parentOrigin.includes('localhost:3000')
    ? `${parentOrigin}/#/loan_information/${loanId}`
    : `${parentOrigin}/nportal#/loan_information/${loanId}`;
};

const toLoanTask = (row: QcDashboardRow): Document =>
  ({
    taskId: row.taskId,
    loanId: row.loanId,
    taskName: DocumentName.POST_CLOSING_REVIEW,
    status: TaskStatus[row.status] ?? row.status,
  } as unknown as Document);

const LoanIdCell = ({ loanId }: { loanId: number }) => {
  const classes = useStyles();
  return (
    <span className={classes.link} onClick={() => window.open(loanUrl(loanId), '_blank')}>
      {loanId}
    </span>
  );
};

const UploadedDateCell = ({ uploadedDate }: { uploadedDate: string }) => {
  const classes = useStyles();
  return uploadedDate
    ? <span>{formatDate(uploadedDate, 'MM/dd/yyyy HH:mm')}</span>
    : <span className={classes.muted}>No documents yet</span>;
};

const DelayCell = observer(({ row }: { row: QcDashboardRow }) => {
  const { qcDashboardStore } = useQcDashboardStore();
  const classes = useStyles();
  const minutes = qcDashboardStore.currentDelayMinutes(row);
  return minutes === null ? <span className={classes.muted}>—</span> : <span>{formatDelay(minutes)}</span>;
});

export const QcDashboard = observer(() => {
  const { qcDashboardStore } = useQcDashboardStore();
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const { gridStore } = qcDashboardStore;
  const classes = useStyles();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: QcDashboardRow }>(null);
  const [completeRow, setCompleteRow] = useState<QcDashboardRow>(null);
  const [revisionRow, setRevisionRow] = useState<QcDashboardRow>(null);
  const gridApi = useRef(null);

  useEffect(() => {
    gridStore.fetchGridData();
    const timer = setInterval(() => qcDashboardStore.tick(), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fitColumns = () => gridApi.current?.sizeColumnsToFit();
    window.addEventListener('resize', fitColumns);
    return () => window.removeEventListener('resize', fitColumns);
  }, []);

  const closeMenu = () => setMenu(null);

  const currentRow = (row: QcDashboardRow): QcDashboardRow =>
    (gridStore.gridData.data.rows as QcDashboardRow[]).find(candidate => candidate.taskId === row.taskId) ?? row;

  const openReview = (row: QcDashboardRow) => {
    qcDashboardStore.setReviewRow(row);
    if (!globalStore.userFeatures) {
      userStore.getUserInfoAndEntitlements();
    }
  };

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    loanIdCellRenderer: ({ value }) => <LoanIdCell loanId={value} />,
    qcSpecialistCellRenderer: ({ value }) => value ? <span>{value}</span> : <span className={classes.muted}>Unassigned</span>,
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip
          label={TaskStatus[value] ?? value}
          displayName={QC_STATUS_OPTIONS.find(option => option.value === value)?.label}
          size="small"
          statusType={StatusType.GENERAL_TASK_STATUS}
        />
      ) : null,
    delayCellRenderer: ({ data }) => <DelayCell row={data} />,
    uploadedDateCellRenderer: ({ value }) => <UploadedDateCell uploadedDate={value} />,
    actionsCellRenderer: ({ data }) => (
      <IconButton size="small" onClick={e => setMenu({ anchor: e.currentTarget, row: currentRow(data) })}>
        <MoreVert color="primary" />
      </IconButton>
    ),
  };

  const menuRowBusy = !!menu?.row && qcDashboardStore.isBusy(menu.row.loanId);
  const menuRowFlagged = (menu?.row?.itemsForRevision ?? 0) > 0;
  const menuRowCompleted = menu?.row?.status === QC_COMPLETED_STATUS;
  const canSendRevision = menuRowFlagged && !menuRowBusy && !menuRowCompleted;
  const canComplete = !menuRowFlagged && !menuRowBusy && !menuRowCompleted;
  const completeTooltip = menuRowCompleted ? REVIEW_ALREADY_COMPLETED : menuRowFlagged ? FLAGGED_ITEMS_BLOCK_COMPLETION : '';
  const sendRevisionTooltip = menuRowCompleted ? REVIEW_ALREADY_COMPLETED : canSendRevision ? '' : NO_ITEMS_FOR_REVISION;

  return (
    <Layout title="Post QC Dashboard">
      <DataGrid
        gridReady={params => {
          gridApi.current = params.api;
          params.api.sizeColumnsToFit();
        }}
        firstDataRendered={params => params.api.sizeColumnsToFit()}
        columns={columns}
        rows={gridStore.gridData.data.rows}
        frameworkComponents={frameworkComponents}
        filterModel={gridStore.filterModel}
        showFilters
        showPagination
        paginationData={gridStore.gridData.meta}
        setPageNumber={page => gridStore.setPageNumber(page)}
        onSortChanged={params => gridStore.setSortModel(params.api.getSortModel())}
        onFilterChanged={params => gridStore.setFilterModel(params.api.getFilterModel())}
        isLoading={gridStore.isLoading}
      />
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={closeMenu} keepMounted={false}>
        <MenuItem
          onClick={() => {
            openReview(menu.row);
            closeMenu();
          }}
        >
          <ListItemIcon className={classes.menuIcon}><OpenInNew fontSize="small" /></ListItemIcon>
          Open
        </MenuItem>
        <Tooltip title={completeTooltip} placement="left">
          <span>
            <MenuItem
              disabled={!canComplete}
              onClick={() => {
                setCompleteRow(menu.row);
                closeMenu();
              }}
            >
              <ListItemIcon className={classes.menuIcon}><CheckCircleOutline fontSize="small" /></ListItemIcon>
              Mark as Completed
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={sendRevisionTooltip} placement="left">
          <span>
            <MenuItem
              disabled={!canSendRevision}
              onClick={() => {
                setRevisionRow(menu.row);
                closeMenu();
              }}
            >
              <ListItemIcon className={classes.menuIcon}><Reply fontSize="small" /></ListItemIcon>
              Send Revision
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <ConfirmDialog
        open={!!completeRow}
        title="Mark as Completed"
        body={<Typography>Please confirm you want to mark the QC Review for loan {completeRow?.loanId} as completed.</Typography>}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        handleConfirm={() => {
          const loanId = completeRow.loanId;
          setCompleteRow(null);
          qcDashboardStore.completeReview(loanId);
        }}
        handleCancel={() => setCompleteRow(null)}
        handleClose={() => setCompleteRow(null)}
      />
      <ConfirmDialog
        open={!!revisionRow}
        title="Send Revision"
        body={<Typography>Please confirm you want to send the revision document for loan {revisionRow?.loanId} to the Closing chat.</Typography>}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        handleConfirm={() => {
          const loanId = revisionRow.loanId;
          setRevisionRow(null);
          qcDashboardStore.sendRevision(loanId);
        }}
        handleCancel={() => setRevisionRow(null)}
        handleClose={() => setRevisionRow(null)}
      />
      {qcDashboardStore.reviewRow && globalStore.userFeatures && (
        <PostQcReviewDialog
          open
          loanId={qcDashboardStore.reviewRow.loanId}
          loanTask={toLoanTask(qcDashboardStore.reviewRow)}
          onClose={() => {
            qcDashboardStore.setReviewRow(null);
            gridStore.fetchGridData();
          }}
        />
      )}
    </Layout>
  );
});
